/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: user.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { type RequestOptions, type Transport } from '../transport';

import type {
  ChangeEmailRequest,
  ChangeEmailResponse,
  CopyUserPasswordRequest,
  CopyUserPasswordResponse,
  CreateUserRequest,
  CreateUserResponse,
  DeactivateUserRequest,
  DeactivateUserResponse,
  DeleteUserRequest,
  DeleteUserResponse,
  GetCurrentUserRequest,
  GetCurrentUserResponse,
  GetCurrentUserSkillsRequest,
  GetCurrentUserSkillsResponse,
  GetMyAvailabilityRequest,
  GetMyAvailabilityResponse,
  GetUserRequest,
  GetUserResponse,
  GetUserSkillsRequest,
  GetUserSkillsResponse,
  HeartbeatMyAvailabilityRequest,
  HeartbeatMyAvailabilityResponse,
  LookupUsersRequest,
  LookupUsersResponse,
  ReactivateUserRequest,
  ReactivateUserResponse,
  SearchUsersRequest,
  SearchUsersResponse,
  SetMyAvailabilityRequest,
  SetMyAvailabilityResponse,
  SetPasswordRequest,
  SetPasswordResponse,
  SetUserSkillsRequest,
  SetUserSkillsResponse,
  UpdateCurrentUserRequest,
  UpdateCurrentUserResponse,
  UpdateUserRequest,
  UpdateUserResponse,
} from '../types/user';

export const createUserService = (transport: Transport) => ({
  async getCurrentUser(
    _request: GetCurrentUserRequest,
    options: RequestOptions = {},
  ): Promise<GetCurrentUserResponse> {
    const url = getApiPath(API_METHODS.UserService.GetCurrentUser);
    return transport.get<GetCurrentUserResponse>(url, options);
  },

  async updateCurrentUser(
    request: UpdateCurrentUserRequest,
    options: RequestOptions = {},
  ): Promise<UpdateCurrentUserResponse> {
    const url = getApiPath(API_METHODS.UserService.UpdateCurrentUser);
    return transport.post<UpdateCurrentUserResponse>(url, request, options);
  },

  async getUser(request: GetUserRequest, options: RequestOptions = {}): Promise<GetUserResponse> {
    const { user_id } = request;
    const url = getApiPath(API_METHODS.UserService.GetUser, { user_id });
    return transport.get<GetUserResponse>(url, options);
  },

  async searchUsers(
    request: SearchUsersRequest,
    options: RequestOptions = {},
  ): Promise<SearchUsersResponse> {
    const url = getApiPath(API_METHODS.UserService.SearchUsers);
    return transport.get<SearchUsersResponse>(url, { params: request, ...options });
  },

  async lookupUsers(
    request: LookupUsersRequest,
    options: RequestOptions = {},
  ): Promise<LookupUsersResponse> {
    const url = getApiPath(API_METHODS.UserService.LookupUsers);
    return transport.get<LookupUsersResponse>(url, { params: request, ...options });
  },

  async createUser(
    request: CreateUserRequest,
    options: RequestOptions = {},
  ): Promise<CreateUserResponse> {
    const url = getApiPath(API_METHODS.UserService.CreateUser);
    return transport.post<CreateUserResponse>(url, request, options);
  },

  async setPassword(
    request: SetPasswordRequest,
    options: RequestOptions = {},
  ): Promise<SetPasswordResponse> {
    const { user_id, ...body } = request;
    const url = getApiPath(API_METHODS.UserService.SetPassword, { user_id });
    return transport.post<SetPasswordResponse>(url, body, options);
  },

  async updateUser(
    request: UpdateUserRequest,
    options: RequestOptions = {},
  ): Promise<UpdateUserResponse> {
    const { user_id, ...body } = request;
    const url = getApiPath(API_METHODS.UserService.UpdateUser, { user_id });
    return transport.post<UpdateUserResponse>(url, body, options);
  },

  async getCurrentUserSkills(
    _request: GetCurrentUserSkillsRequest,
    options: RequestOptions = {},
  ): Promise<GetCurrentUserSkillsResponse> {
    const url = getApiPath(API_METHODS.UserService.GetCurrentUserSkills);
    return transport.get<GetCurrentUserSkillsResponse>(url, options);
  },

  async getUserSkills(
    request: GetUserSkillsRequest,
    options: RequestOptions = {},
  ): Promise<GetUserSkillsResponse> {
    const { organization_id, user_id } = request;
    const url = getApiPath(API_METHODS.UserService.GetUserSkills, { organization_id, user_id });
    return transport.get<GetUserSkillsResponse>(url, options);
  },

  async setUserSkills(
    request: SetUserSkillsRequest,
    options: RequestOptions = {},
  ): Promise<SetUserSkillsResponse> {
    const { organization_id, user_id, ...body } = request;
    const url = getApiPath(API_METHODS.UserService.SetUserSkills, { organization_id, user_id });
    return transport.post<SetUserSkillsResponse>(url, body, options);
  },

  async setMyAvailability(
    request: SetMyAvailabilityRequest,
    options: RequestOptions = {},
  ): Promise<SetMyAvailabilityResponse> {
    const url = getApiPath(API_METHODS.UserService.SetMyAvailability);
    return transport.post<SetMyAvailabilityResponse>(url, request, options);
  },

  async getMyAvailability(
    _request: GetMyAvailabilityRequest,
    options: RequestOptions = {},
  ): Promise<GetMyAvailabilityResponse> {
    const url = getApiPath(API_METHODS.UserService.GetMyAvailability);
    return transport.get<GetMyAvailabilityResponse>(url, options);
  },

  async heartbeatMyAvailability(
    _request: HeartbeatMyAvailabilityRequest,
    options: RequestOptions = {},
  ): Promise<HeartbeatMyAvailabilityResponse> {
    const url = getApiPath(API_METHODS.UserService.HeartbeatMyAvailability);
    return transport.post<HeartbeatMyAvailabilityResponse>(url, {}, options);
  },

  async deleteUser(
    request: DeleteUserRequest,
    options: RequestOptions = {},
  ): Promise<DeleteUserResponse> {
    const { user_id } = request;
    const url = getApiPath(API_METHODS.UserService.DeleteUser, { user_id });
    return transport.delete<DeleteUserResponse>(url, options);
  },

  async changeEmail(
    request: ChangeEmailRequest,
    options: RequestOptions = {},
  ): Promise<ChangeEmailResponse> {
    const { user_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminUserService.ChangeEmail, { user_id });
    return transport.post<ChangeEmailResponse>(url, body, options);
  },

  async deactivateUser(
    request: DeactivateUserRequest,
    options: RequestOptions = {},
  ): Promise<DeactivateUserResponse> {
    const { user_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminUserService.DeactivateUser, { user_id });
    return transport.post<DeactivateUserResponse>(url, body, options);
  },

  async reactivateUser(
    request: ReactivateUserRequest,
    options: RequestOptions = {},
  ): Promise<ReactivateUserResponse> {
    const { user_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminUserService.ReactivateUser, { user_id });
    return transport.post<ReactivateUserResponse>(url, body, options);
  },

  async copyUserPassword(
    request: CopyUserPasswordRequest,
    options: RequestOptions = {},
  ): Promise<CopyUserPasswordResponse> {
    const { user_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminUserService.CopyUserPassword, { user_id });
    return transport.post<CopyUserPasswordResponse>(url, body, options);
  },
});

export type UserService = ReturnType<typeof createUserService>;
